/**
 * The mosque's «Kontakt» page in the member app.  Owner, 2026-10-30.
 *
 * "Members should open Kontakt and see the website, the address, the phone, the e-mail, the map, and the people to
 * contact — with what they are: Imam, Styreleder, Kasserer." Staff write it in the panel («Kontaktinfo», settings.write);
 * everybody reads it (GET /v1/mosque/contact). Two parts:
 *
 *   profile   the mosque's own address, phone, e-mail and website — each optional. An EMPTY field falls back to the
 *             organisation's own value in Brønnøysund (the platform reads it once a week, irn-catalogue.js), and the
 *             answer says per field where it came from: 'mosque', 'brreg' or null. Brønnøysund has no address here.
 *   people    at most MAX_PEOPLE, in the order staff put them: a name, a designation (optional), a phone, an e-mail. They
 *             are public on purpose — the panel says «Bare personer som har sagt ja til å vises i appen». With no person
 *             entered, IRN's contact person is shown instead, but only where the platform's operator switched it on
 *             (the platform passes it or null), and with no designation (IRN does not say what the person is).
 *   listed    the contacts staff ticked «Vis i appen» in «Kontakter» (contacts.js, MKK module 13, owner 2026-10-30 —
 *             only while that module is on, never archived ones; the API reads them). They come AFTER the people above,
 *             by name, with their role as the designation; the whole list is at most MAX_LISTED. Staff-only fields of
 *             the directory (organisation, notes, tags, languages) are never passed here.
 *
 * The rules, here once: a phone is +, digits and spaces, 5–20 long (kept as typed: «22 33 44 55», «+47 400 00 000»); an
 * e-mail is lower-cased; a website is https only — «www.x.no» becomes «https://www.x.no», anything with another
 * scheme (javascript:, http:, mailto:), a user name or password, a space, or no dot in the host is refused.
 *
 * Pure: no I/O.
 */
'use strict';
const { oneLine, CONTROL } = require('./fields');

const MAX_PEOPLE = 20;
/** The people on the public page altogether: MAX_PEOPLE of the page's own, then contacts ticked in «Kontakter». */
const MAX_LISTED = 40;
const MAX_NAME = 120;
const MAX_DESIGNATION = 80;
const MAX_ADDRESS = 200;
const MAX_EMAIL = 254;
const MAX_WEBSITE = 200;
const PHONE_MIN = 5;
const PHONE_MAX = 20;
const PROFILE_FIELDS = ['address', 'phone', 'email', 'website'];
const PERSON_FIELDS = ['name', 'designation', 'phone', 'email'];
/** Brønnøysund's fields: the fallback for an empty one of these. */
const OFFICIAL_FIELDS = ['phone', 'email', 'website'];

const empty = (v) => v === undefined || v === null || (typeof v === 'string' && !v.trim());
const ok = (value) => ({ ok: true, value });
const no = (message) => ({ ok: false, message });

/** A phone as staff typed it: whitespace runs collapse; +, digits and spaces only, 5–20 long. → { ok, value|null }. */
function phone(v) {
  if (empty(v)) return ok(null);
  if (typeof v !== 'string') return no('Telefonnummeret må være tekst.');
  const s = v.replace(/\s+/g, ' ').trim();
  if (!/^\+?[0-9][0-9 ]*$/.test(s) || s.length < PHONE_MIN || s.length > PHONE_MAX) {
    return no(`Telefonnummeret kan bare ha +, siffer og mellomrom (${PHONE_MIN}–${PHONE_MAX} tegn).`);
  }
  return ok(s);
}
/** An e-mail address: trimmed, lower-cased, one «@», a dot in the domain. */
function email(v) {
  if (empty(v)) return ok(null);
  if (typeof v !== 'string') return no('E-postadressen må være tekst.');
  const s = v.trim().toLowerCase();
  if (s.length > MAX_EMAIL || !/^[^\s@<>"()]+@[^\s@<>"()]+\.[a-z]{2,24}$/.test(s)) return no('E-postadressen ser ikke riktig ut.');
  return ok(s);
}
/**
 * A website the app opens in the browser: https only. «www.x.no» or «x.no/om» → «https://…»; the scheme is written in
 * lower case. Refused: another scheme (javascript:, http:, data:), a user name or password, whitespace, quotes or
 * angle brackets, a host without a dot.
 */
function website(v) {
  if (empty(v)) return ok(null);
  if (typeof v !== 'string') return no('Nettsiden må være tekst.');
  let s = v.trim();
  const bad = no('Nettsiden må være en https-adresse, for eksempel https://www.moskeen.no.');
  if (s.length > MAX_WEBSITE || /[\s<>"'`\\]/.test(s) || CONTROL.test(s)) return bad;
  if (/^https:\/\//i.test(s)) s = 'https://' + s.slice(8);
  else if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return bad;     // javascript:, http:, mailto:, «localhost:80» — any other scheme
  else if (s.startsWith('//')) return bad;
  else s = 'https://' + s;
  if (s.length > MAX_WEBSITE) return bad;
  let u;
  try { u = new URL(s); } catch { return bad; }
  if (u.protocol !== 'https:' || u.username || u.password || !/^[a-z0-9.-]+$/i.test(u.hostname) || !/\.[a-z]{2,}$/i.test(u.hostname)) return bad;
  return ok(s);
}

/** The keys of an object that are not on `allowed`. */
const unknown = (o, allowed) => Object.keys(o).filter((k) => !allowed.includes(k));
const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

/** The mosque's own fields. → { ok, value: {address, phone, email, website} } | { ok: false, errors } (keys as sent). */
function profile(src) {
  const p = src === undefined || src === null ? {} : src;
  if (!isObj(p)) return { ok: false, errors: { profile: 'Ugyldig kontaktinfo.' } };
  const extra = unknown(p, PROFILE_FIELDS);
  if (extra.length) return { ok: false, errors: Object.fromEntries(extra.map((k) => [`profile.${k}`, 'Ukjent felt.'])) };
  const errors = {}, value = {};
  const take = (k, r) => { if (r.ok) value[k] = r.value; else errors[`profile.${k}`] = r.message; };
  take('address', oneLine(p.address, 'en adresse', MAX_ADDRESS, false));
  take('phone', phone(p.phone));
  take('email', email(p.email));
  take('website', website(p.website));
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
}

/** One person, the `i`th on the list. */
function person(src, i) {
  const at = `people.${i}`;
  if (!isObj(src)) return { ok: false, errors: { [at]: 'Ugyldig person.' } };
  const extra = unknown(src, PERSON_FIELDS);
  if (extra.length) return { ok: false, errors: Object.fromEntries(extra.map((k) => [`${at}.${k}`, 'Ukjent felt.'])) };
  const errors = {}, value = {};
  const take = (k, r) => { if (r.ok) value[k] = r.value; else errors[`${at}.${k}`] = r.message; };
  take('name', oneLine(src.name, 'et navn', MAX_NAME, true));
  take('designation', oneLine(src.designation, 'en stilling eller rolle', MAX_DESIGNATION, false));
  take('phone', phone(src.phone));
  take('email', email(src.email));
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
}

/**
 * The whole page as the panel sends it (PUT replaces both parts): { profile: {...}, people: [...] }.
 * → { ok, value: { profile, people } } | { ok: false, errors } — every field checked, unknown ones refused, the list at
 * most MAX_PEOPLE long.
 */
function input(src) {
  if (!isObj(src)) return { ok: false, errors: { page: 'Ugyldig kontaktinfo.' } };
  const extra = unknown(src, ['profile', 'people']);
  if (extra.length) return { ok: false, errors: Object.fromEntries(extra.map((k) => [k, 'Ukjent felt.'])) };
  const list = src.people === undefined || src.people === null ? [] : src.people;
  if (!Array.isArray(list)) return { ok: false, errors: { people: 'Kontaktpersonene må være en liste.' } };
  if (list.length > MAX_PEOPLE) return { ok: false, errors: { people: `Høyst ${MAX_PEOPLE} kontaktpersoner.` }, code: 'too_many_people' };
  const errors = {};
  const p = profile(src.profile);
  if (!p.ok) Object.assign(errors, p.errors);
  const people = [];
  list.forEach((x, i) => { const r = person(x, i); if (r.ok) people.push(r.value); else Object.assign(errors, r.errors); });
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: { profile: p.value, people } };
}

/**
 * The public answer. `own` = { profile: {address, phone, email, website} | null, people: [...] } as stored; `official` =
 * Brønnøysund's { website, phone, email } or null; `irnPerson` = { name, phone, email } (the IRN contact person the
 * operator chose to show) or null; `listed` = the «Kontakter» contacts ticked «Vis i appen» as { name, roleTitle, phone,
 * email } (any order; sorted here by name, Norwegian collation) or nothing. Staff-entered fields win; an empty one falls
 * back to Brønnøysund's, which is checked by the same rules (a value that fails is no value). People: the page's own in
 * their order, then the listed ones (a name already on the page is not repeated), at most MAX_LISTED; IRN's person only when BOTH are empty.
 * → { address, phone, email, website, people, sources }.
 */
function merge(own, official, irnPerson, listed) {
  const mine = (own && own.profile) || {};
  const out = { address: null, phone: null, email: null, website: null };
  const sources = { address: null, phone: null, email: null, website: null, people: null };
  const RULE = { phone, email, website };
  for (const k of PROFILE_FIELDS) {
    const v = empty(mine[k]) ? null : mine[k];
    if (v) { out[k] = v; sources[k] = 'mosque'; continue; }
    if (OFFICIAL_FIELDS.includes(k) && official && !empty(official[k])) {
      const r = RULE[k](official[k]);
      if (r.ok && r.value) { out[k] = r.value; sources[k] = 'brreg'; }
    }
  }
  let people = ((own && own.people) || []).slice(0, MAX_PEOPLE).map((x) => ({ name: x.name, designation: x.designation || null,
    phone: x.phone || null, email: x.email || null }));
  const seen = new Set(people.map((x) => String(x.name).trim().toLowerCase()));
  const fromDirectory = (Array.isArray(listed) ? listed : []).filter((x) => x && !empty(x.name) && !seen.has(String(x.name).trim().toLowerCase()))
    .sort((a, b) => String(a.name).localeCompare(String(b.name), 'nb'))
    .map((x) => ({ name: x.name, designation: x.roleTitle || null, phone: x.phone || null, email: x.email || null }));
  people = people.concat(fromDirectory).slice(0, MAX_LISTED);
  if (people.length) sources.people = 'mosque';
  else if (irnPerson) {
    const name = oneLine(irnPerson.name, 'et navn', MAX_NAME, false), ph = phone(irnPerson.phone), em = email(irnPerson.email);
    const one = { name: name.ok ? name.value : null, designation: null, phone: ph.ok ? ph.value : null, email: em.ok ? em.value : null };
    if (one.name || one.phone || one.email) { people = [one]; sources.people = 'irn'; }
  }
  return { ...out, people, sources };
}

/**
 * What the audit may keep of a page (through audit.js contactPageAudit): how many people, their names and designations,
 * which of the mosque's own fields are filled in — never a phone, an e-mail, the address or the website.
 */
function auditView(page) {
  const prof = (page && page.profile) || {};
  const people = (page && page.people) || [];
  return { contactpage_people: people.length,
    contactpage_names: people.map((x) => ({ name: x.name, designation: x.designation || null })),
    contactpage_filled: PROFILE_FIELDS.filter((k) => !empty(prof[k])) };
}

/** What changed between two pages, by name: the profile fields, and 'people' when the list differs in any way. */
function changed(before, after) {
  const a = (before && before.profile) || {}, b = (after && after.profile) || {};
  const out = PROFILE_FIELDS.filter((k) => (a[k] || null) !== (b[k] || null));
  const norm = (l) => JSON.stringify((l || []).map((x) => PERSON_FIELDS.map((k) => x[k] || null)));
  if (norm(before && before.people) !== norm(after && after.people)) out.push('people');
  return out;
}

module.exports = { MAX_PEOPLE, MAX_LISTED, MAX_NAME, MAX_DESIGNATION, MAX_ADDRESS, MAX_EMAIL, MAX_WEBSITE, PHONE_MAX,
  phone, email, website, profile, person, input, merge, auditView, changed };
